import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoryPage = () => {
  const { slug } = useParams();
  const actualSlug = slug || 'all'; // Default to 'all' if no slug (for /products route)
  const { addToCart, getCartCount } = useCart();
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [maxPrice, setMaxPrice] = useState(50000);
  const [categoryName, setCategoryName] = useState('Kategori'); // Dynamic category name

  // Fetch category name from API
  useEffect(() => {
    const fetchCategoryName = async () => {
      if (actualSlug === 'all') {
        setCategoryName('Tüm Ürünler');
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/categories/${actualSlug}`);
        if (response.ok) {
          const data = await response.json();
          setCategoryName(data.name);
        } else {
          setCategoryName('Kategori');
        }
      } catch (error) {
        console.error('Error fetching category name:', error);
        setCategoryName('Kategori');
      }
    };

    fetchCategoryName();
  }, [actualSlug]);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Pass category to backend for recursive search
        const categoryParam = actualSlug !== 'all' ? `?category=${actualSlug}` : '';
        const response = await fetch(`${API_URL}/api/products${categoryParam}`);
        const data = await response.json();
        setAllProducts(data);
        
        // Calculate max price
        const max = data.length > 0 ? Math.max(...data.map(p => p.price)) : 50000;
        setMaxPrice(Math.ceil(max / 1000) * 1000); // Round up to nearest 1000
        setPriceRange([0, Math.ceil(max / 1000) * 1000]);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [actualSlug]);

  // Filter and sort products
  useEffect(() => {
    if (!allProducts.length) return;

    let filtered = allProducts;

    // Backend already handles category filtering (including recursive subcategories)
    // No need to filter by category here

    // Apply price filter
    filtered = filtered.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Apply sorting - prioritize display_order
    switch (sortBy) {
      case 'price-low':
        filtered = [...filtered].sort((a, b) => {
          if (a.display_order && b.display_order) return a.display_order - b.display_order;
          if (a.display_order) return -1;
          if (b.display_order) return 1;
          return a.price - b.price;
        });
        break;
      case 'price-high':
        filtered = [...filtered].sort((a, b) => {
          if (a.display_order && b.display_order) return a.display_order - b.display_order;
          if (a.display_order) return -1;
          if (b.display_order) return 1;
          return b.price - a.price;
        });
        break;
      case 'discount':
        filtered = [...filtered].sort((a, b) => {
          if (a.display_order && b.display_order) return a.display_order - b.display_order;
          if (a.display_order) return -1;
          if (b.display_order) return 1;
          return (b.discount || 0) - (a.discount || 0);
        });
        break;
      case 'new':
        filtered = [...filtered].sort((a, b) => {
          if (a.display_order && b.display_order) return a.display_order - b.display_order;
          if (a.display_order) return -1;
          if (b.display_order) return 1;
          return (b.new ? 1 : 0) - (a.new ? 1 : 0);
        });
        break;
      default:
        // Featured: prioritize display_order first
        filtered = [...filtered].sort((a, b) => {
          if (a.display_order && b.display_order) return a.display_order - b.display_order;
          if (a.display_order) return -1;
          if (b.display_order) return 1;
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        });
        break;
    }

    setFilteredProducts(filtered);
  }, [sortBy, priceRange, allProducts]);

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    alert(`${product.name} sepete eklendi!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header cartCount={getCartCount()} />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${categoryName} - Cep Premium`}
        description={`${categoryName} kategorisindeki tüm ürünleri keşfedin. Türkiye garantili, hızlı kargo ve güvenli alışveriş.`}
        keywords={`${categoryName}, cep premium, teknoloji ürünleri, online alışveriş`}
      />
      <Header cartCount={getCartCount()} />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm">
            <a href="/" className="text-gray-600 hover:text-red-600">Ana Sayfa</a>
            <span className="text-gray-400">/</span>
            <span className="text-gray-800">{categoryName}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{categoryName}</h1>
            <p className="text-gray-600 mt-1">{filteredProducts.length} ürün bulundu</p>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-600 cursor-pointer"
              >
                <option value="featured">Önerilen</option>
                <option value="new">Yeni Ürünler</option>
                <option value="price-low">Fiyat (Düşükten Yüksek)</option>
                <option value="price-high">Fiyat (Yüksekten Düşüğe)</option>
                <option value="discount">En Çok İndirim</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <SlidersHorizontal size={20} className="text-red-600" />
                <h3 className="font-semibold text-lg">Filtreler</h3>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Fiyat Aralığı</h4>
                <div className="space-y-3">
                  <input
                    type="range"
                    min="0"
                    max={maxPrice}
                    step="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full accent-red-600"
                  />
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">0 TL</span>
                    <span className="font-medium">{priceRange[1].toLocaleString('tr-TR')} TL</span>
                  </div>
                </div>
              </div>

              {/* Category Info */}
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>{filteredProducts.length}</strong> ürün görüntüleniyor
                </p>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-24 w-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg mb-2">Bu kategoride ürün bulunamadı.</p>
                <p className="text-gray-400 text-sm">Lütfen farklı bir kategori deneyin veya filtreleri değiştirin.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CategoryPage;
